// src/pages/Onboarding.js
import React, { useState, useContext, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { saveOnboardingStep, completeOnboardingApi, getOnboardingData } from '../api/onboarding';
import { vfUpdateVariables } from '../api/VoiceflowInteractions';
import { ONBOARDING_STEPS } from '../components/onboarding/onboardingData';

// Screens
import IntroScreen from '../components/onboarding/screens/IntroScreen';
import ActivitesScreen from '../components/onboarding/screens/ActivitesScreen';
import MatieresScreen from '../components/onboarding/screens/MatieresScreen';
import SpecialitesScreen from '../components/onboarding/screens/SpecialitesScreen';
import MetiersScreen from '../components/onboarding/screens/MetiersScreen';
import EtatScreen from '../components/onboarding/screens/EtatScreen';
import AttentesScreen from '../components/onboarding/screens/AttentesScreen';
import FierteScreen from '../components/onboarding/screens/FierteScreen';
import TransmissionScreen from '../components/onboarding/screens/TransmissionScreen';
import FinalScreen from '../components/onboarding/screens/FinalScreen';

const Onboarding = () => {
  const navigate = useNavigate();
  const { user, completeOnboarding } = useContext(AuthContext);

  const [currentStep, setCurrentStep] = useState(ONBOARDING_STEPS.INTRO);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);

  // Données de l'onboarding
  const [answers, setAnswers] = useState({
    activites: [],
    matieres: [],
    specialites: [],
    mathsOption: '',
    metiersAvant: '',
    metiersAujourdhui: '',
    etatPerso: '',
    etatExterieur: '',
    attentes: [],
    fierte: ''
  });

  // Récupérer les données existantes au chargement
  useEffect(() => {
    const loadExistingData = async () => {
      try {
        const data = await getOnboardingData();
        if (data) {
          // Récupérer les spécialités selon la classe ([] est truthy, donc on vérifie la longueur)
          const rawSpecialites = data.premiere_specialities?.length > 0
            ? data.premiere_specialities
            : (data.terminale_specialities || []);

          setAnswers(prev => ({
            ...prev,
            activites: data.activites || [],
            matieres: data.matieres || [],
            specialites: rawSpecialites.filter(s => s !== 'Maths expertes' && s !== 'Maths complémentaires'),
            mathsOption: rawSpecialites.includes('Maths expertes') ? 'expertes' : rawSpecialites.includes('Maths complémentaires') ? 'complementaires' : '',
            metiersAvant: data.metiers_avant || '',
            metiersAujourdhui: data.metiers_aujourdhui || '',
            etatPerso: data.etat_perso || '',
            etatExterieur: data.etat_exterieur || '',
            attentes: data.attentes || [],
            fierte: data.fierte || ''
          }));
          // Reprendre à la dernière étape non complétée
          if (data.current_step) {
            setCurrentStep(data.current_step);
          }
        }
      } catch (e) {
        // Pas de données existantes, on commence du début
        console.log('No existing onboarding data');
      } finally {
        setInitialLoading(false);
      }
    };
    loadExistingData();
  }, []);

  // Sauvegarder une étape
  const saveStep = useCallback(async (step, data) => {
    setLoading(true);
    setError(null);
    try {
      await saveOnboardingStep(step, data);
      return true;
    } catch (e) {
      console.error('Failed to save step:', e);
      setError("Une erreur est survenue. Reessaie.");
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Handlers pour chaque écran
  const handleIntroNext = () => {
    setCurrentStep(ONBOARDING_STEPS.ACTIVITES);
  };

  const handleActivitesNext = async () => {
    const success = await saveStep('activites', { activites: answers.activites });
    if (success) {
      setCurrentStep(ONBOARDING_STEPS.MATIERES);
    }
  };

  const handleMatieresNext = async () => {
    const success = await saveStep('matieres', { matieres: answers.matieres });
    if (success) {
      const classe = user?.school_level;
      if (classe === 'premiere' || classe === 'terminale') {
        setCurrentStep(ONBOARDING_STEPS.SPECIALITES);
      } else {
        setCurrentStep(ONBOARDING_STEPS.METIERS);
      }
    }
  };

  const handleSpecialitesNext = async () => {
    const classe = user?.school_level;
    const fieldName = classe === 'premiere' ? 'premiere_specialities' : 'terminale_specialities';

    // Concaténer l'option maths au tableau des spécialités
    let finalSpecialites = [...answers.specialites];
    if (answers.mathsOption === 'expertes') {
      finalSpecialites.push('Maths expertes');
    } else if (answers.mathsOption === 'complementaires') {
      finalSpecialites.push('Maths complémentaires');
    }

    const success = await saveStep('specialites', {
      [fieldName]: finalSpecialites
    });
    if (success) {
      setCurrentStep(ONBOARDING_STEPS.METIERS);
    }
  };

  const handleMetiersNext = async () => {
    const success = await saveStep('metiers', {
      metiers_avant: answers.metiersAvant,
      metiers_aujourdhui: answers.metiersAujourdhui
    });
    if (success) {
      setCurrentStep(ONBOARDING_STEPS.ETAT);
    }
  };

  const handleEtatNext = async () => {
    const success = await saveStep('etat', {
      etat_perso: answers.etatPerso,
      etat_exterieur: answers.etatExterieur
    });
    if (success) {
      setCurrentStep(ONBOARDING_STEPS.ATTENTES);
    }
  };

  const handleAttentesNext = async () => {
    const success = await saveStep('attentes', { attentes: answers.attentes });
    if (success) {
      setCurrentStep(ONBOARDING_STEPS.FIERTE);
    }
  };

  const handleFierteNext = async () => {
    const success = await saveStep('fierte', { fierte: answers.fierte });
    if (success) {
      setCurrentStep('transmission');
    }
  };

  const handleTransmissionComplete = async () => {
    try {
      await completeOnboardingApi();

      // Mettre à jour les variables LUX avec les données d'onboarding
      const luxSessionId = `lux_${user.id}`;
      const luxVariables = {
        matieres_aimees: answers.matieres.join(", "),
        activites_hors_ecole: answers.activites.join(", "),
        moment_fierte: answers.fierte,
        etat_perso: answers.etatPerso,
        etat_exterieur: answers.etatExterieur,
        attentes: answers.attentes.join(", "),
        specialites: answers.mathsOption
          ? [...answers.specialites, `Maths ${answers.mathsOption}`].join(", ")
          : answers.specialites.join(", "),
        envies_metiers_niveau: answers.metiersAujourdhui
      };
      await vfUpdateVariables(luxSessionId, luxVariables, process.env.REACT_APP_VF_LUX_API_KEY);

      completeOnboarding(user.firstName, user.gender);
      setCurrentStep(ONBOARDING_STEPS.FINAL);
    } catch (e) {
      console.error('Failed to complete onboarding:', e);
      setError("Une erreur est survenue. Reessaie.");
    }
  };

  const handleStart = () => {
    navigate('/mon-espace');
  };

  // Affichage loading initial
  if (initialLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-purple-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  // Rendu des écrans
  switch (currentStep) {
    case ONBOARDING_STEPS.INTRO:
      return (
        <IntroScreen
          userName={user?.firstName || 'toi'}
          onNext={handleIntroNext}
        />
      );

    case ONBOARDING_STEPS.ACTIVITES:
      return (
        <ActivitesScreen
          activites={answers.activites}
          onChange={(activites) => setAnswers(prev => ({ ...prev, activites }))}
          onNext={handleActivitesNext}
          loading={loading}
          error={error}
        />
      );

    case ONBOARDING_STEPS.MATIERES:
      return (
        <MatieresScreen
          matieres={answers.matieres}
          classe={user?.school_level || 'seconde'}
          onChange={(matieres) => setAnswers(prev => ({ ...prev, matieres }))}
          onNext={handleMatieresNext}
          loading={loading}
          error={error}
        />
      );

    case ONBOARDING_STEPS.SPECIALITES:
      return (
        <SpecialitesScreen
          specialites={answers.specialites}
          mathsOption={answers.mathsOption}
          classe={user?.school_level}
          onChange={(data) => setAnswers(prev => ({ ...prev, ...data }))}
          onNext={handleSpecialitesNext}
          loading={loading}
          error={error}
        />
      );

    case ONBOARDING_STEPS.METIERS:
      return (
        <MetiersScreen
          metiersAvant={answers.metiersAvant}
          metiersAujourdhui={answers.metiersAujourdhui}
          genre={user?.gender}
          onChange={(data) => setAnswers(prev => ({ ...prev, ...data }))}
          onNext={handleMetiersNext}
          loading={loading}
          error={error}
        />
      );

    case ONBOARDING_STEPS.ETAT:
      return (
        <EtatScreen
          etatPerso={answers.etatPerso}
          etatExterieur={answers.etatExterieur}
          genre={user?.gender}
          onChange={(data) => setAnswers(prev => ({ ...prev, ...data }))}
          onNext={handleEtatNext}
          loading={loading}
          error={error}
        />
      );

    case ONBOARDING_STEPS.ATTENTES:
      return (
        <AttentesScreen
          attentes={answers.attentes}
          onChange={(attentes) => setAnswers(prev => ({ ...prev, attentes }))}
          onNext={handleAttentesNext}
          loading={loading}
          error={error}
        />
      );

    case ONBOARDING_STEPS.FIERTE:
      return (
        <FierteScreen
          fierte={answers.fierte}
          genre={user?.gender}
          onChange={(fierte) => setAnswers(prev => ({ ...prev, fierte }))}
          onNext={handleFierteNext}
          loading={loading}
          error={error}
        />
      );

    case 'transmission':
      return (
        <TransmissionScreen
          onComplete={handleTransmissionComplete}
          error={error}
        />
      );

    case ONBOARDING_STEPS.FINAL:
      return (
        <FinalScreen
          userName={user?.firstName || 'toi'}
          onStart={handleStart}
        />
      );

    default:
      return null;
  }
};

export default Onboarding;
